<template>
  <div class="draggable-list">
    <table class="table">
      <thead>
        <tr>
          <th v-for="column in columns" :key="column.key" :style="column.style || {}">
            {{ column.label }}
          </th>
          <th v-if="showActions" style="width: 150px;">操作</th>
        </tr>
      </thead>
      <draggable 
        v-model="internalItems" 
        tag="tbody" 
        @end="onDragEnd"
        :disabled="!!editingItem"
        itemKey="id"
      >
        <template #item="{ element, index }">
          <tr class="draggable-row">
            <td v-for="column in columns" :key="column.key" :style="column.style || {}">
              <div v-if="column.key === 'drag' && (!editingItem || editingItem.id !== element.id)" class="drag-handle">
                <i class="fas fa-bars"></i>
              </div>
              <template v-else-if="editingItem && editingItem.id === element.id && column.key === 'maker'">
                <input v-model="editingItem.maker" class="form-control" />
              </template>
              <template v-else>
                {{ element[column.key] }}
              </template>
            </td>
            <td v-if="showActions" class="actions-cell">
              <div v-if="editingItem && editingItem.id === element.id" class="action-buttons">
                <button @click="saveEdit()" class="btn btn-primary btn-sm">保存</button>
                <button @click="cancelEdit()" class="btn btn-secondary btn-sm">キャンセル</button>
              </div>
              <div v-else class="action-buttons">
                <button 
                  v-show="showMoveUp" 
                  @click="moveUp(index)" 
                  class="btn btn-icon" 
                  :disabled="index === 0"
                  :style="{ opacity: index === 0 ? 0.3 : 1, pointerEvents: index === 0 ? 'none' : 'auto' }"
                >
                  <i class="fas fa-arrow-up"></i>
                </button>
                <button 
                  v-show="showMoveDown" 
                  @click="moveDown(index)" 
                  class="btn btn-icon" 
                  :disabled="index === internalItems.length - 1"
                  :style="{ 
                    opacity: index === internalItems.length - 1 ? 0.3 : 1, 
                    pointerEvents: index === internalItems.length - 1 ? 'none' : 'auto' 
                  }"
                >
                  <i class="fas fa-arrow-down"></i>
                </button>
                <button v-if="showEdit" @click="startEdit(element)" class="btn btn-icon">
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  v-if="showDelete"
                  @click="confirmDelete(element)"
                  class="btn btn-icon"
                  :disabled="disableDelete"
                  :style="{ opacity: disableDelete ? 0.3 : 1, pointerEvents: disableDelete ? 'none' : 'auto' }"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </template>
      </draggable>
    </table>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import draggable from 'vuedraggable';

export default defineComponent({
  name: 'FormDraggableList',
  components: {
    draggable
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showMoveUp: {
      type: Boolean,
      default: true
    },
    showMoveDown: {
      type: Boolean,
      default: true
    },
    showEdit: {
      type: Boolean,
      default: true
    },
    showDelete: {
      type: Boolean,
      default: true
    },
    disableDelete: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:items', 'order-changed', 'save-edit', 'delete-item'],
  data() {
    return {
      internalItems: [...this.items],
      editingItem: null,
      originalItem: null
    };
  },
  watch: {
    items: {
      handler(newItems) {
        this.internalItems = [...newItems];
      },
      deep: true
    }
  },
  methods: {
    onDragEnd() {
      this.$emit('update:items', this.internalItems);
      this.$emit('order-changed', this.internalItems);
    },
    moveUp(index) {
      if (0 < index) {
        const newItems = [...this.internalItems];
        const temp = newItems[index];
        newItems[index] = newItems[index - 1];
        newItems[index - 1] = temp;
        this.internalItems = newItems;
        this.$emit('update:items', this.internalItems);
        this.$emit('order-changed', this.internalItems);
      }
    },
    moveDown(index) {
      if (index < this.internalItems.length - 1) {
        const newItems = [...this.internalItems];
        const temp = newItems[index];
        newItems[index] = newItems[index + 1];
        newItems[index + 1] = temp;
        this.internalItems = newItems;
        this.$emit('update:items', this.internalItems);
        this.$emit('order-changed', this.internalItems);
      }
    },
    startEdit(item) {
      this.originalItem = { ...item };
      this.editingItem = { ...item };
    },
    saveEdit() {
      if (!this.editingItem) return;
      
      this.$emit('save-edit', this.editingItem);
      this.editingItem = null;
      this.originalItem = null;
    },
    cancelEdit() {
      if (!this.editingItem || !this.originalItem) {
        this.editingItem = null;
        this.originalItem = null;
        return;
      }
      
      const index = this.internalItems.findIndex(item => item.id === this.editingItem.id);
      if (-1 !== index) {
        this.internalItems[index] = { ...this.originalItem };
        this.$emit('update:items', this.internalItems);
      }
      
      this.editingItem = null;
      this.originalItem = null;
    },
    confirmDelete(item) {
      if (!confirm(`「${item.maker}」を削除してもよろしいですか？`)) return;
      
      this.$emit('delete-item', item);
    }
  }
});
</script>

<style scoped>
.draggable-list {
  width: 100%;
}

.draggable-row {
  cursor: move;
}

.drag-handle {
  padding: 5px;
}

.actions-cell {
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-icon {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--gray-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 49px;
  height: 49px;
  box-sizing: border-box;
}

.table th {
  font-weight: bold;
  background-color: #195983;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 49px;
  height: 49px;
  box-sizing: border-box;
}
</style>
